import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Typography } from '@mui/material';
import { colors } from 'app/components/ThemeProvider/themeColors';
import { CustomSwitch } from 'app/components/ui/Switch';
import InputField from 'app/shared/ui/elements/Input';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { httpClient } from '../../../../api/http/http-client';
import { AsanConfirmation, toAsanSession } from 'app/features/sign/asan';
import { digitalAccountApiUrl } from '../../../../constants/enviroments';
import { DIGITAL_ACCOUNT_ROUTES } from '../../../../constants/routes';
import { useCustomMutation } from '../../../../hooks/digital/useMutation';
import { useCustomQuery } from '../../../../hooks/digital/useQuery';
import { PrintFormsResponse } from '../../../../shared/types/digital-account/steps';
import { useDigitalContext } from '../../../../context/digital';
import { useCustomNavigate } from '../../../../utils/customHooks/useCustomNavigate';
import i18n from '../../../../utils/translation/i18n';
import DocCard from './components/doc-card';
import PdfModal from './components/pdf-modal';

const schema = yup.object({
  email: yup
    .string()
    .email(i18n.t('anipay.schema_wrong_email_format'))
    .required(i18n.t('anipay.schema_email_format_required')),
});

type FormData = yup.InferType<typeof schema>;
export default function DocsSign() {
  const { state } = useDigitalContext();
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [showEmailField, setShowEmailField] = useState(false);
  const [asanConfirmation, setAsanConfirmation] = useState(false);
  const navigate = useCustomNavigate();

  const {
    control,
    getValues,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      email: '',
    },
    resolver: yupResolver(schema),
  });

  const { data } = useCustomQuery({
    queryKey: 'print-forms-fetch',
    queryFn: async (): Promise<PrintFormsResponse> =>
      await httpClient.post(
        `${digitalAccountApiUrl}/onboarding?business_key=${state.business_key}&access_token=${state.business_key}`,
        {
          stage: 'print-forms',
        }
      ),
  });

  const {
    mutate: signMutate,
    data: asanData,
    isLoading,
  } = useCustomMutation(async (data: { action: string; email: string }) => {
    // Your API call here
    // Example: return await httpClient.post('/api/consent/sign', { business_key: state.business_key });
    return await httpClient.post<any>(
      `${digitalAccountApiUrl}/onboarding?business_key=${state.business_key}&access_token=${state.business_key}`,
      data
    ); // Simulated API call
  });

  const { mutate: readFile } = useCustomMutation(async (doc_id: number) => {
    // Your API call here
    // Example: return await httpClient.post('/api/consent/sign', { business_key: state.business_key });
    return await httpClient.post<{
      business_key: string;
      file_url: string;
      result: string;
    }>(
      `${digitalAccountApiUrl}/onboarding?business_key=${state.business_key}&access_token=${state.business_key}`,
      {
        action: 'read',
        doc_id,
      }
    ); // Simulated API call
  });

  const openPdfModal = (id: number) => {
    readFile(id, {
      onSuccess: response => {
        setSelectedFile(response.file_url);
      },
    });
  };

  const sign = async () => {
    const triggerValidation = showEmailField ? await trigger(['email']) : true;

    if (!triggerValidation) {
      return;
    }

    signMutate(
      {
        action: 'sign',
        email: getValues('email'),
      },
      {
        onSuccess: response => {
          if (response.result === 'asan_wait') {
            setAsanConfirmation(true);

            // navigate(
            //   `${DIGITAL_ACCOUNT_ROUTES.base}/${DIGITAL_ACCOUNT_ROUTES.progress}/${DIGITAL_ACCOUNT_ROUTES['video-call']}`
            // );
            return;
          }
          if (data.print_forms) {
            setAsanConfirmation(false);
          }
        },
        onError: error => {
          setAsanConfirmation(false);
          console.error('Error signing consent:', error);
        },
      }
    );
  };

  const handleRedirect = (data?: any) => {
    if (data?.stage === 'videocall') {
      navigate(
        `${DIGITAL_ACCOUNT_ROUTES.base}/${DIGITAL_ACCOUNT_ROUTES.progress}/${DIGITAL_ACCOUNT_ROUTES['videocall']}`
      );
    }
  };

  return (
    <>
      <Typography fontSize={28} lineHeight={'36px'} fontWeight={600}>
        Sənədlərin imzalanması
      </Typography>

      <Box sx={{ display: 'flex', gap: '12px', flexDirection: 'column', mt: '32px' }}>
        {(data?.print_forms || []).map((printForm, index) => (
          <DocCard
            key={printForm.url}
            title={printForm.title}
            onClick={() => openPdfModal(index)}
          />
        ))}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          marginTop: '32px',
        }}
      >
        <Typography>Sənədləri e-poçt ünvanıma göndər</Typography>
        <CustomSwitch
          onChange={(_, checked) => {
            if (!checked) setValue('email', '');
            setShowEmailField(checked);
          }}
          checked={showEmailField}
          trackColor={colors.brand[400]}
        />
      </Box>

      <form style={{ display: 'flex', marginTop: '16px', flexDirection: 'column' }}>
        {showEmailField && (
          <Controller
            control={control}
            name='email'
            render={({ field }) => (
              <InputField
                labelText='E-poçt ünvan'
                placeholder='support@yelo.az'
                error={!!errors.email}
                errorText={errors.email?.message}
                {...field}
              />
            )}
          />
        )}

        <Button
          disabled={isLoading}
          onClick={sign}
          sx={{ padding: '16px !important', marginTop: '24px' }}
        >
          {isLoading ? 'Gözləyin...' : 'Sənədləri oxudum və imzalayıram'}
        </Button>
      </form>
      <AsanConfirmation
        open={asanConfirmation}
        close={setAsanConfirmation}
        session={toAsanSession(asanData)}
        handleRedirect={handleRedirect}
        repeat_action='resend'
      />
      <PdfModal file={selectedFile} close={() => setSelectedFile(null)} />
    </>
  );
}
